import {
	PropsWithChildren,
	ReactElement,
	useEffect,
	useState
} from "react";
import { useActivePanelOverrideStore } from "./useActivePanelOverrideStore";
import { shallowComparison } from "client/Misc/compareUtils";
import { ContainsDropOversWrapper, Portal } from "client/Components";
import { MenuItemKind } from "./GlobalNavigationSidebar";
import { Action } from "client_legacy/ndaw/actions";
import { SubNavigation } from "./SubNavigation";
import styles from "./ActivePanelOverride.module.less";

export type ActivePanelOverrideProps<TActionContext,> = {
	title: string;
	kind: MenuItemKind;
} & (
	{
		actions?: undefined;
		actionContext?: undefined;
	}
	| {
		actions: readonly Action<TActionContext>[];
		actionContext: TActionContext;
	}
);

export const ActivePanelOverride = <TActionContext,>(
	props: PropsWithChildren<ActivePanelOverrideProps<TActionContext>>
): ReactElement | null => {
	const {
		title,
		kind,
		actionContext,
		actions,
		children
	} = props;

	const [host, setHost] = useState<HTMLElement | null>(null);

	const {
		registerOverride,
		clearOverride,
		setDoesOverrideContainDropOvers
	} = useActivePanelOverrideStore(
		x => ({
			registerOverride: x.registerOverride,
			clearOverride: x.clearOverride,
			setDoesOverrideContainDropOvers: x.setDoesOverrideContainDropOvers
		}),
		shallowComparison
	);

	useEffect(
		(): EffectWithDestructorResult => {
			const host: HTMLElement | null = registerOverride();

			setHost(host);

			return () => {
				setHost(null);
				clearOverride();
			};
		},
		[clearOverride, registerOverride]
	);

	if (!host) {
		return null;
	}

	return (
		<Portal container={host}>
			<ContainsDropOversWrapper onDoesContainDropOversChange={setDoesOverrideContainDropOvers}>
				<div
					className={styles.root}
				>
					<SubNavigation.Header
						title={title}
						kind={kind}
						actions={actions}
						actionContext={actionContext!}
					/>
					<SubNavigation.Body>
						{children}
					</SubNavigation.Body>
				</div>
			</ContainsDropOversWrapper>
		</Portal>
	);
};
ActivePanelOverride.displayName = "ActivePanelOverride";